import { useState } from 'react';
import { SettingsSectionCard } from '@/components/settings/SettingsSectionCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Pencil } from 'lucide-react';

interface AdministratorCardProps {
    fullName?: string;
    email?: string;
    phone?: string;
    role?: string;
    showBadge?: boolean;
    badgeText?: string;
    isLoading?: boolean;
    canEdit?: boolean;
    companyId?: string;
    onSave?: (data: { fullName: string; phone: string }) => Promise<void>;
}

export function AdministratorCard({
    fullName = 'Loading...',
    email = 'Loading...',
    phone = '',
    role = 'Admin',
    showBadge = true,
    badgeText = 'Primary account administrator',
    isLoading = false,
    canEdit = false,
    onSave,
}: AdministratorCardProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(fullName);
    const [editPhone, setEditPhone] = useState(phone);
    const [isSaving, setIsSaving] = useState(false);

    const handleEdit = () => {
        setEditName(fullName);
        setEditPhone(phone);
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setEditName(fullName);
        setEditPhone(phone);
    };

    const handleSave = async () => {
        if (!onSave) return;
        setIsSaving(true);
        try {
            await onSave({ fullName: editName, phone: editPhone });
            setIsEditing(false);
        } catch {
            // Error handling done by parent via toast
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <SettingsSectionCard
            title="Company Administrator"
            description="Primary account owner contact information"
            actions={
                canEdit && !isEditing && !isLoading ? (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleEdit}
                        className="flex items-center gap-2"
                    >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                    </Button>
                ) : undefined
            }
        >
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                        Full Name
                    </label>
                    {isEditing ? (
                        <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Full Name"
                        />
                    ) : (
                        <p className="font-medium text-foreground">
                            {isLoading ? 'Loading...' : fullName}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                        Email
                    </label>
                    <p className="font-medium text-foreground">
                        {isLoading ? 'Loading...' : email}
                    </p>
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                        Telephone Number
                    </label>
                    {isEditing ? (
                        <Input
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            placeholder="(555) 123-4567"
                        />
                    ) : (
                        <p className="font-medium text-foreground">
                            {isLoading ? 'Loading...' : (phone || '—')}
                        </p>
                    )}
                </div>

                <div className="space-y-2">
                    <label className="block text-sm font-medium text-foreground">
                        Role in Your Company
                    </label>
                    <p className="font-medium text-foreground">{role}</p>
                </div>
            </div>

            {isEditing && (
                <div className="mt-6 flex items-center gap-3">
                    <Button
                        onClick={handleSave}
                        disabled={isSaving}
                        size="sm"
                    >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        disabled={isSaving}
                        size="sm"
                    >
                        Cancel
                    </Button>
                </div>
            )}

            {showBadge && (
                <div className="mt-6 flex items-center gap-2 rounded-md border border-outline-variant bg-muted/40 px-4 py-3">
                    <Check className="h-5 w-5 shrink-0 text-primary" />
                    <span className="text-sm font-medium text-foreground">
                        {badgeText}
                    </span>
                </div>
            )}
        </SettingsSectionCard>
    );
}
